<template>
  <div class="rounded-md border p-6">
    <div class="mb-4 flex items-center justify-between">
      <div>
        <h3 class="text-lg font-semibold">Price Forecast</h3>
        <p class="text-sm text-muted-foreground">
          {{ horizon }}-day forecast{{ formattedDate ? ` (${formattedDate})` : '' }}
        </p>
      </div>
      <div v-if="loading" class="flex items-center gap-2">
        <svg
          class="h-4 w-4 animate-spin text-muted-foreground"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>
    </div>

    <div v-if="error" class="rounded-md bg-destructive/10 p-4 text-sm text-destructive">
      {{ error }}
    </div>

    <div v-else-if="forecast" class="space-y-4">
      <!-- Current Price -->
      <div class="flex items-center justify-between border-b pb-3">
        <span class="text-sm text-muted-foreground">Current Price</span>
        <span class="text-lg font-semibold">${{ forecast.current_price.toFixed(2) }}</span>
      </div>

      <!-- Day-by-Day Forecast -->
      <div v-if="forecast.forecast.forecast_days && forecast.forecast.forecast_days.length > 0" class="space-y-2">
        <p class="text-sm font-medium text-muted-foreground">Day-by-Day Forecast</p>
        <div class="space-y-2">
          <div
            v-for="(price, index) in forecast.forecast.forecast_days"
            :key="index"
            class="flex items-center justify-between rounded-md border p-3"
          >
            <div class="flex flex-col">
              <span class="text-sm font-medium">Day {{ index + 1 }}</span>
              <span class="text-xs text-muted-foreground">{{ getDayDate(index + 1) }}</span>
            </div>
            <div class="flex items-center gap-4">
              <div class="text-right">
                <span class="text-sm font-semibold">${{ price.toFixed(2) }}</span>
                <div class="text-xs text-muted-foreground">
                  {{ getDayReturn(price).toFixed(2) }}%
                </div>
              </div>
              <div
                v-if="forecast.forecast.q20_days && forecast.forecast.q80_days && forecast.forecast.q20_days[index] && forecast.forecast.q80_days[index]"
                class="flex flex-col text-right text-xs text-muted-foreground"
              >
                <span>Q20: ${{ forecast.forecast.q20_days[index].toFixed(2) }}</span>
                <span>Q80: ${{ forecast.forecast.q80_days[index].toFixed(2) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

        <!-- Forecast Range -->
        <div class="space-y-3">
          <div>
            <div class="mb-2 flex items-center justify-between text-sm">
              <span class="text-muted-foreground">Forecast Range</span>
              <span class="font-medium">${{ forecast.forecast.mean_price.toFixed(2) }}</span>
            </div>
            <div class="relative h-8 w-full rounded-md bg-muted">
              <!-- Background range indicator -->
              <div
                class="absolute inset-y-0 flex items-center justify-center rounded-md"
                :class="forecast.forecast.mean_return >= 0 ? 'bg-green-500/20' : 'bg-red-500/20'"
                :style="{
                  left: '20%',
                  width: '60%'
                }"
              ></div>
              <!-- Mean indicator -->
              <div
                class="absolute inset-y-0 flex items-center justify-center"
                :style="{ left: '50%', transform: 'translateX(-50%)' }"
              >
                <div
                  class="h-full w-0.5"
                  :class="forecast.forecast.mean_return >= 0 ? 'bg-green-500' : 'bg-red-500'"
                ></div>
              </div>
              <!-- Mean value label -->
              <div
                class="absolute inset-y-0 flex items-center justify-center"
                :style="{ left: '50%', transform: 'translateX(-50%)' }"
              >
                <span class="text-xs font-medium">
                  {{ (forecast.forecast.mean_return * 100).toFixed(2) }}%
                </span>
              </div>
            </div>
            <div class="mt-1 flex justify-between text-xs text-muted-foreground">
              <span>Q20: {{ (forecast.forecast.q20_return * 100).toFixed(2) }}%</span>
              <span>Q80: {{ (forecast.forecast.q80_return * 100).toFixed(2) }}%</span>
            </div>
          </div>

        <!-- Model Breakdown -->
        <div class="rounded-md bg-muted/50 p-3">
          <p class="mb-2 text-xs font-medium text-muted-foreground">Model Forecasts</p>
          <div class="space-y-1">
            <template
              v-for="(model, name) in forecast.models"
              :key="name"
            >
              <div
                v-if="model && typeof model === 'object' && typeof model.mean === 'number'"
                class="flex items-center justify-between text-sm"
              >
                <span class="capitalize text-muted-foreground">{{ name }}</span>
                <span class="font-medium">${{ model.mean.toFixed(2) }}</span>
              </div>
            </template>
          </div>
        </div>

        <!-- Confidence -->
        <div class="flex items-center justify-between">
          <span class="text-sm text-muted-foreground">Confidence</span>
          <div class="flex items-center gap-2">
            <div class="h-2 w-24 overflow-hidden rounded-full bg-muted">
              <div
                class="h-full transition-all"
                :class="
                  forecast.confidence >= 0.7
                    ? 'bg-green-500'
                    : forecast.confidence >= 0.5
                    ? 'bg-yellow-500'
                    : 'bg-red-500'
                "
                :style="{ width: `${forecast.confidence * 100}%` }"
              ></div>
            </div>
            <span class="text-sm font-medium">{{ (forecast.confidence * 100).toFixed(0) }}%</span>
          </div>
        </div>

        <!-- StockTwits Sentiment -->
        <div v-if="forecast.sentiment" class="rounded-md bg-muted/50 p-3">
          <div class="mb-2 flex items-center justify-between">
            <p class="text-xs font-medium text-muted-foreground">StockTwits Sentiment</p>
            <span class="text-xs text-muted-foreground">{{ forecast.sentiment.count }} messages</span>
          </div>
          <div class="space-y-2">
            <div class="flex items-center justify-between text-sm">
              <span class="text-muted-foreground">Current Sentiment</span>
              <span
                class="font-medium"
                :class="
                  forecast.sentiment.current > 0.1
                    ? 'text-green-600'
                    : forecast.sentiment.current < -0.1
                    ? 'text-red-600'
                    : 'text-muted-foreground'
                "
              >
                {{ (forecast.sentiment.current * 100).toFixed(1) }}%
              </span>
            </div>
            <div class="flex items-center justify-between text-sm">
              <span class="text-muted-foreground">3-Day EMA</span>
              <span
                class="font-medium"
                :class="
                  forecast.sentiment.ema_3 > 0.1
                    ? 'text-green-600'
                    : forecast.sentiment.ema_3 < -0.1
                    ? 'text-red-600'
                    : 'text-muted-foreground'
                "
              >
                {{ (forecast.sentiment.ema_3 * 100).toFixed(1) }}%
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed, onUnmounted } from 'vue'
import { apiClient, type ForecastResponse, type ForecastJobStatusResponse } from '@/lib/api'

interface Props {
  symbol: string
  horizon?: number
}

const props = withDefaults(defineProps<Props>(), {
  horizon: 5
})

const forecast = ref<ForecastResponse | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)
const asof = ref<string>('')
let pollInterval: ReturnType<typeof setInterval> | null = null

const formattedDate = computed(() => {
  if (!asof.value) return ''
  try {
    const date = new Date(asof.value)
    if (isNaN(date.getTime())) return ''
    return date.toLocaleDateString()
  } catch {
    return ''
  }
})

const getDayDate = (dayOffset: number): string => {
  if (!asof.value) return ''
  try {
    const baseDate = new Date(asof.value)
    if (isNaN(baseDate.getTime())) return ''
    const forecastDate = new Date(baseDate)
    forecastDate.setDate(forecastDate.getDate() + dayOffset)
    return forecastDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  } catch {
    return ''
  }
}

const getDayReturn = (price: number): number => {
  if (!forecast.value) return 0
  return ((price - forecast.value.current_price) / forecast.value.current_price) * 100
}

const pollJobStatus = async (jobId: string) => {
  if (pollInterval) {
    clearInterval(pollInterval)
  }
  
  pollInterval = setInterval(async () => {
    try {
      const status = await apiClient.getForecastJobStatus(jobId)
      
      if (status.status === 'completed' && status.result) {
        forecast.value = status.result
        asof.value = status.result.asof
        loading.value = false
        if (pollInterval) {
          clearInterval(pollInterval)
          pollInterval = null
        }
      } else if (status.status === 'failed') {
        error.value = status.error || 'Forecast job failed'
        loading.value = false
        if (pollInterval) {
          clearInterval(pollInterval)
          pollInterval = null
        }
      }
    } catch (err: any) {
      console.error('Error polling forecast job status:', err)
      if (pollInterval) {
        clearInterval(pollInterval)
        pollInterval = null
      }
      loading.value = false
      error.value = 'Failed to get job status'
    }
  }, 1000) // Poll every second for quick jobs
}

const fetchForecast = async () => {
  loading.value = true
  error.value = null
  forecast.value = null
  
  try {
    // Start background job
    const jobResponse = await apiClient.startForecastJob(props.symbol, props.horizon)
    
    // Poll for completion
    await pollJobStatus(jobResponse.job_id)
  } catch (err: any) {
    error.value = err.response?.data?.detail || err.message || 'Failed to start forecast job'
    loading.value = false
    console.error('Forecast error:', err)
  }
}

onMounted(() => {
  fetchForecast()
})

watch(() => [props.symbol, props.horizon], () => {
  fetchForecast()
})

onUnmounted(() => {
  if (pollInterval) {
    clearInterval(pollInterval)
    pollInterval = null
  }
})
</script>

